import React, { useState } from "react";
import {
  EditIcon,
  RemoveIcon,
  DetailsIcon,
  SortIcon,
  ArrowLeft,
  ArrowRight
} from "./CustomTableSvg";
import "./CustomTable.scss";
import utilsFunction from "../../utils/utilsFunction";
import { toast } from "react-toastify";

const CustomTable = ({
  tableData,
  minWidth,
  onEdit,
  onChangeStatus,
  onRemove,
  onDetails,
  onRedirect,
  hasOperation = true,
  hasDetails = false
}) => {

  const { columns = [], data = [], priceFields = [] } = tableData;

  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: null,
  });

  const rowsPerPage = 20;

  // Filtering logic
  const filteredData = data.filter((row) =>
    columns.every((col) => {
      if (filters[col.field]) {
        const filterValue = filters[col.field].trim().toLowerCase();

        if (col.isSelectOption && col.selectOptions) {
          const optionLabel = col.selectOptions
            .find((option) => String(option.value) === String(row[col.field]))
            ?.label.toLowerCase();
          return optionLabel && optionLabel.includes(filterValue);
        }

        const rowValue = row[col.field]?.toString().trim().toLowerCase();
        return rowValue.includes(filterValue);
      }
      return true;
    })
  );

  // Sum logic
  const calculateSum = (field, filteredData) => {
    return filteredData.reduce((total, row) => {
      const value = parseFloat(row[field]);
      return total + (isNaN(value) ? 0 : value);
    }, 0);
  };

  // Sorting logic
  const sortedData = [...filteredData].sort((a, b) => {

    if (!sortConfig.field) return 0;
    const { field, direction } = sortConfig;

    const valueA = a[field] || "";
    const valueB = b[field] || "";

    // Handle Persian/English sorting
    const collator = new Intl.Collator("fa", {
      numeric: true,
      sensitivity: "base",
    });
    const comparison = collator.compare(valueA.toString(), valueB.toString());

    return direction === "asc" ? comparison : -comparison;
  });

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const formatPrice = (value) =>
    value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleFilterChange = (e, field) => {
    const test = utilsFunction.convertToEnglishNumerals(e.target.value);
    setFilters((prev) => ({ ...prev, [field]: test }));
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        if (prev.direction === "asc") {
          return { field, direction: "desc" };
        } else if (prev.direction === "desc") {
          return { field: null, direction: null }; // Reset sorting
        }
      }
      return { field, direction: "asc" };
    });
  };

  // Copy to Clipboard Handler
  const handleCopy = (value) => {
    navigator.clipboard.writeText(value).then(() => {
      toast.success("بارکد پستی کپی شد");
    });
  };

  return (
    <>
      <div className="custom-table-container">
        <div
          className="custom-table"
          style={{ minWidth: minWidth ? minWidth + "px" : "1000px" }}
        >
          {/* Header */}
          <ul className="custom-table-header">
            <li className="custom-table-row">
              <div style={{ width: "70px", flexShrink: 0 }}>ردیف</div>
              <div className="dynamic-column">
                {columns
                  .filter((col) => col.visible)
                  .map((col) => (
                    <div key={col.field} style={{ width: `${col.width}%` }}>
                      {col.title}
                      <button
                        className="sort-btn"
                        onClick={() => handleSort(col.field)}
                      >
                        {sortConfig.field === col.field ? (
                          sortConfig.direction === "asc" ? (
                            <SortIcon />
                          ) : (
                            <SortIcon />
                          )
                        ) : (
                          <SortIcon />
                        )}
                      </button>
                    </div>
                  ))}
              </div>
              {hasOperation && (
                <div style={{ width: "70px", flexShrink: 0 }}>عملیات</div>
              )}
            </li>
          </ul>

          {/* First Row (Input Row) */}
          <div className="first-row-parent">
            <li className="custom-table-row first-row">
              <div style={{ width: "70px", flexShrink: 0 }}></div>
              <div className="dynamic-column">
                {columns
                  .filter((col) => col.visible)
                  .map((col) => (
                    <div key={col.field} style={{ width: `${col.width}%` }}>
                      {col.headerFilter && (
                        <input
                          type="text"
                          value={filters[col.field] || ""}
                          onChange={(e) => handleFilterChange(e, col.field)}
                        // placeholder={`فیلتر ${col.title}`}
                        />
                      )}
                    </div>
                  ))}
              </div>
              {hasOperation && (
                <div style={{ width: "70px", flexShrink: 0 }}></div>
              )}
            </li>
          </div>
          {/* Sum Numbers */}
          {priceFields?.length !== 0 && (
            <div className="first-row-parent sum-price">
              <li className="custom-table-row first-row">
                <div style={{ width: "70px", flexShrink: 0 }}></div>
                <div className="dynamic-column">
                  {columns
                    .filter((col) => col.visible)
                    .map((col) => (
                      <div key={col.field} style={{ width: `${col.width}%` }}>
                        {priceFields.includes(col.field) && (
                          formatPrice(calculateSum(col.field, filteredData))
                        )}
                      </div>
                    ))}
                </div>
                {hasOperation && (
                  <div style={{ width: "70px", flexShrink: 0 }}></div>
                )}
              </li>
            </div>
          )}

          {/* Body */}
          <ul className="custom-table-body">
            {currentData.map((row, index) => (
              <li key={index} className="custom-table-row">
                {/* Row Number */}
                <div style={{ width: "70px", flexShrink: 0 }}>
                  {startIndex + index + 1}
                </div>
                {/* Dynamic Columns */}
                <div className="dynamic-column">
                  {columns
                    .filter((col) => col.visible)
                    .map((col) => (
                      <div key={col.field} style={{ width: `${col.width}%` }}>
                        {col.isSelectOption && col.selectOptions ? (
                          !col.canChange ? (
                            <span
                              style={{
                                color:
                                  col.selectOptions.find(
                                    (option) =>
                                      String(option.value) ===
                                      String(row[col.field])
                                  )?.colorCode || "#000",
                              }}
                            >
                              {
                                col.selectOptions.find(
                                  (option) =>
                                    String(option.value) ===
                                    String(row[col.field])
                                )?.label
                              }
                            </span>
                          ) : (
                            <select
                              value={row[col.field] || ""}
                              onChange={(e) =>
                                onChangeStatus(row, e.target.value)
                              }
                              style={{
                                color:
                                  col.selectOptions.find(
                                    (option) =>
                                      String(option.value) ===
                                      String(row[col.field])
                                  )?.colorCode || "#000",
                              }}
                            >
                              {col.selectOptions.map((option) => (
                                <option
                                  key={option.id}
                                  value={option.value}
                                  style={{ color: option.colorCode || "#000" }}
                                >
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          )
                        ) : priceFields.includes(col.field) ? (
                          formatPrice(row[col.field])
                        ) : col.copy ? (
                          <span
                            className="copyable"
                            onClick={() => handleCopy(row[col.field])}
                          >
                            {row[col.field]}
                          </span>
                        ) : col.isLink ? (
                          <span
                            className="link-column"
                            onClick={() => onRedirect(row.shop_id, col.field)}
                          >
                            {row[col.field]}
                          </span>
                        ) : (
                          row[col.field]
                        )}
                      </div>
                    ))}
                </div>
                {/* Operations */}
                {hasOperation && (
                  <div style={{ width: "70px", flexShrink: 0 }}>
                    <div className="custom-table-operation">
                      {row.canEdit !== undefined && row.canEdit !== 0 && (
                        <button className="edit" onClick={() => onEdit(row)}>
                          <EditIcon />
                        </button>
                      )}
                      {row.canDelete !== undefined && row.canDelete !== 0 && (
                        <button
                          className="remove"
                          onClick={() => onRemove(row)}
                        >
                          <RemoveIcon />
                        </button>
                      )}
                      {hasDetails && (
                        <button
                          className="details"
                          onClick={() => onDetails(row)}
                        >
                          <DetailsIcon />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="table-pagination">
        <div className="table-pagination-box">
          <p>نمایش {rowsPerPage} مورد</p>
          <button onClick={handlePreviousPage} disabled={currentPage === 1}>
            <ArrowRight />
          </button>
          <span>
            <span className="pagination-num">{currentPage}</span> از{" "}
            {totalPages} تا
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            <ArrowLeft />
          </button>
        </div>
      </div>
    </>
  );
};

export default CustomTable;
